import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2, Upload } from "lucide-react";
import {
  createResource,
  deleteResource,
  listResources,
  swapResourceOrder,
  updateResource,
  type ConciergeResourceAdmin,
} from "@/lib/admin/concierge-api";
import {
  uploadConciergeVideo,
  ALLOWED_VIDEO_MIME,
  MAX_VIDEO_BYTES,
} from "@/lib/admin/concierge-media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";

const TYPES = ["video", "report", "podcast", "link"] as const;

export function ResourcesPanel({ siteId, orgId }: { siteId: string; orgId: string }) {
  const qc = useQueryClient();
  const key = ["concierge", "admin", "resources", siteId];
  const { data = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => listResources(siteId),
    staleTime: 30_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const createM = useMutation({
    mutationFn: () =>
      createResource({
        site_id: siteId,
        type: "link",
        title: "New resource",
        sort_order: (data[data.length - 1]?.sort_order ?? 0) + 1,
      }),
    onSuccess: invalidate,
    onError: (e: any) => toast({ title: "Failed to add", description: e.message, variant: "destructive" }),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => deleteResource(id),
    onSuccess: invalidate,
  });

  const swapM = useMutation({
    mutationFn: ({ a, b }: { a: ConciergeResourceAdmin; b: ConciergeResourceAdmin }) =>
      swapResourceOrder(a, b),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-heading font-semibold">Resources</h2>
        <Button size="sm" onClick={() => createM.mutate()} disabled={createM.isPending}>
          <Plus className="h-4 w-4 mr-1" /> Add resource
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin opacity-60" />
        </div>
      ) : data.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          No resources yet. Add a video, podcast, report, or link.
        </p>
      ) : (
        <div className="space-y-2">
          {data.map((r, i) => (
            <ResourceRow
              key={r.id}
              r={r}
              orgId={orgId}
              siteId={siteId}
              onUp={i === 0 ? undefined : () => swapM.mutate({ a: r, b: data[i - 1] })}
              onDown={i === data.length - 1 ? undefined : () => swapM.mutate({ a: r, b: data[i + 1] })}
              onDelete={() => deleteM.mutate(r.id)}
              onSaved={invalidate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ResourceRow({
  r,
  orgId,
  siteId,
  onUp,
  onDown,
  onDelete,
  onSaved,
}: {
  r: ConciergeResourceAdmin;
  orgId: string;
  siteId: string;
  onUp?: () => void;
  onDown?: () => void;
  onDelete: () => void;
  onSaved: () => void;
}) {
  const [local, setLocal] = useState({
    type: r.type,
    title: r.title ?? "",
    description: r.description ?? "",
    url: r.url ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const dirty =
    local.type !== r.type ||
    (local.title ?? "") !== (r.title ?? "") ||
    (local.description ?? "") !== (r.description ?? "") ||
    (local.url ?? "") !== (r.url ?? "");

  const save = async () => {
    setSaving(true);
    try {
      await updateResource(r.id, {
        type: local.type,
        title: local.title || null,
        description: local.description || null,
        url: local.url || null,
      });
      onSaved();
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (file: File) => {
    setUploading(true);
    try {
      const meta = await uploadConciergeVideo(file, { orgId, siteId, resourceId: r.id });
      await updateResource(r.id, {
        type: "video",
        storage_path: meta.path,
        mime_type: meta.mime_type,
        size_bytes: meta.size_bytes,
        duration_ms: meta.duration_ms ?? undefined,
      });
      toast({ title: "Video uploaded" });
      onSaved();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="grid gap-3 md:grid-cols-[140px_1fr]">
          <Select value={local.type} onValueChange={(v) => setLocal((s) => ({ ...s, type: v }))}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => (
                <SelectItem key={t} value={t} className="capitalize">
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Title"
            value={local.title}
            onChange={(e) => setLocal((s) => ({ ...s, title: e.target.value }))}
          />
        </div>
        <Input
          placeholder="URL (https://…) — or upload a video below"
          value={local.url}
          onChange={(e) => setLocal((s) => ({ ...s, url: e.target.value }))}
        />
        <Textarea
          placeholder="Description (optional)"
          rows={2}
          value={local.description}
          onChange={(e) => setLocal((s) => ({ ...s, description: e.target.value }))}
        />

        {local.type === "video" && (
          <div className="rounded-md border border-dashed p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-muted-foreground">
                {r.storage_path ? (
                  <span>Uploaded file attached · {formatBytes(r.size_bytes)}</span>
                ) : (
                  <span>MP4 / WebM / MOV, up to {Math.round(MAX_VIDEO_BYTES / (1024 * 1024))} MB</span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept={ALLOWED_VIDEO_MIME.join(",")}
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onFile(f);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-1" />
                      {r.storage_path ? "Replace video" : "Upload video"}
                    </>
                  )}
                </Button>
                {r.storage_path && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        await updateResource(r.id, {
                          storage_path: null,
                          mime_type: null,
                          size_bytes: null,
                          duration_ms: null,
                        });
                        onSaved();
                      } catch (e: any) {
                        toast({ title: "Failed", description: e.message, variant: "destructive" });
                      }
                    }}
                  >
                    Detach
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-1">
            <Button variant="outline" size="icon" onClick={onUp} disabled={!onUp} aria-label="Move up">
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={onDown} disabled={!onDown} aria-label="Move down">
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={onDelete} aria-label="Delete">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <Button size="sm" onClick={save} disabled={!dirty || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function formatBytes(n?: number | null): string {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default ResourcesPanel;
